import {
  Box,
  Center,
  Stack,
  Input,
  InputLeftElement,
  Button,
  Flex,
  Text,
  Tooltip,
  Portal,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  Heading,
  Show,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import {
  RESERVATION_TYPES,
  SARAJEVO_INTL_AIRPORT,
  STAY_CITIES,
  VEHICLE_PICKUP_LOCATIONS,
  childrenAgesOptions,
  pickUpTimeInputOptions,
} from "../../constants";
import { dateAfter, getDateInServerExpectedFormat, dateAddDaysCET } from "../../utils/helpers";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import { capitalize } from "lodash";
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot,
  faCalendar,
  faPerson,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import InputGroupWithLabel from "../input-group-with-label/InputGroupWithLabel";
import { useTranslation } from "react-i18next";
import { getCityDualDisplayName, getPickupLocationDisplayName } from "../../utils/tripDisplay";

function ReservationSearchForm({
  reservationType = RESERVATION_TYPES.STAY,
  forHomePage,
  isFetching,
  onSearchButtonClick,
  showCitySelector = true,
  isActive = true,
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");

  const locationRouter = useLocation();
  const [searchParams] = useSearchParams();

  const { isOpen, onToggle, onClose } = useDisclosure();

  useEffect(() => {
    if (!isActive && isOpen) {
      onClose();
    }
  }, [isActive, isOpen, onClose]);

  const {
    location,
    pick_up_time,
    drop_off_time,
    start_date,
    end_date,
    adults,
    rooms,
  } = Object.fromEntries(searchParams.entries());

  const children = searchParams.getAll("children");

  const initialDates = () => {
    let tomorrow = dateAddDaysCET(new Date(), 1);
    let dayAfter = dateAddDaysCET(new Date(), 2);
    return [tomorrow, dayAfter];
  };

  const tourInitialDates = () => {
    let tomorrow = dateAddDaysCET(new Date(), 1);
    let eightDaysLater = dateAddDaysCET(new Date(), 8);
    return [tomorrow, eightDaysLater];
  };

  const [reservation, setReservation] = useState({
    place:
      location ||
      (reservationType === RESERVATION_TYPES.CAR
        ? SARAJEVO_INTL_AIRPORT
        : "Sarajevo"),
    selectedDates:
      start_date && end_date
        ? [
            new Date(start_date.replace(/-/g, "/")),
            new Date(end_date.replace(/-/g, "/")),
          ]
        : reservationType === RESERVATION_TYPES.TOUR
        ? tourInitialDates()
        : initialDates(),
    pickUpTime: pick_up_time,
    dropOffTime: drop_off_time,
    adults: Number(adults) || 2,
    children: children || [],
    rooms: Number(rooms) || 1,
  });

  const tourReservationFormBackgroundColor = "purple.300";
  const carReservationFormBackgroundColor = "teal.300";

  const travelersLabel =
    reservationType === RESERVATION_TYPES.CAR
      ? t("searchPassengersLabel", { count: reservation.adults })
      : reservationType === RESERVATION_TYPES.TOUR
        ? t("searchTourTravelersLabel", {
            adults: reservation.adults,
            children: reservation.children.length,
          })
        : t("searchTravelersLabel", {
            rooms: reservation.rooms,
            adults: reservation.adults,
            children: reservation.children.length,
          });

  const getPopoverTypeLabel = (type) => {
    if (reservationType === RESERVATION_TYPES.CAR) {
      return t("searchPopoverPassengers");
    }
    const labels = {
      rooms: t("roomsLabel"),
      adults: t("searchPopoverAdults"),
      children: t("searchPopoverChildren"),
    };
    return labels[type] ?? type;
  };
  const backgroundImageProps = useBreakpointValue(
    {
      lg: forHomePage
        ? {
            backgroundSize: "cover",
            height: "320px",
            backgroundColor:
              reservationType === RESERVATION_TYPES.TOUR
                ? tourReservationFormBackgroundColor
                : carReservationFormBackgroundColor,
            background:
              reservationType === RESERVATION_TYPES.STAY
                ? "linear-gradient(rgba(0,0,0,0.9), rgba(0,0,0,0)), url('/bosnia-photo.jpg') center/cover no-repeat"
            : reservationType === RESERVATION_TYPES.TOUR
          ? "linear-gradient(rgba(0,0,0,0.9), rgba(0,0,0,0)), url('/tour-bg.jpg') center/cover no-repeat"
      : "",
          }
        : {},
      base: {},
    },
    {
      fallback: "lg",
    }
  );

  const reservationFormStackProps = useBreakpointValue(
    {
      lg: {
        position: forHomePage ? "absolute" : "relative",
        bottom: forHomePage ? "-36px" : "",
      },
      base: {},
    },
    {
      fallback: "lg",
    }
  );

  const searchButtonDisabled = () => {
    if (reservationType === RESERVATION_TYPES.TOUR) {
      return reservation.children.some((age) => age === "");
    } else if (reservationType === RESERVATION_TYPES.STAY) {
      return (
        (showCitySelector && !reservation.place) ||
        reservation.children.some((age) => age === "")
      );
    } else if (reservationType === RESERVATION_TYPES.CAR) {
      return (
        !reservation.pickUpTime ||
        !reservation.place ||
        !reservation.dropOffTime
      );
    }
  };

  return (
        <Box
            position="relative"
            marginBottom={showCitySelector ? "45px" : "0"}
            {...backgroundImageProps}
        >
          <Show above="lg">
            {forHomePage && (
                <Heading
                    padding={"20px"}
                    textAlign="left"
                    fontSize="6xl"
                    color={
                      reservationType === RESERVATION_TYPES.CAR
                        ? "gray.800"
                        : "whiteAlpha.900"
                    }
                >
                  {reservationType === RESERVATION_TYPES.STAY
                      ? t("reservationHeroStay")
                      : reservationType === RESERVATION_TYPES.CAR
                          ? t("reservationHeroCar")
                          : t("reservationHeroTour")}
                </Heading>
            )}
          </Show>
          <Center>
            <Stack
                padding={"20px 15px"}
                boxShadow="base"
                borderRadius={"lg"}
                background="white"
                color="gray.800"
                alignItems="center"
                justifyContent="space-around"
                width={["full", "full", `${forHomePage ? "90%" : "full"}`, "full"]}
                direction={["column", "column", "column", "row"]}
                gap={[10, 10, 5]}
                {...reservationFormStackProps}
            >
              {reservationType !== RESERVATION_TYPES.TOUR && showCitySelector && (
                  <InputGroupWithLabel
                      label={
                        reservationType === RESERVATION_TYPES.CAR
                            ? t("common:pickUpLocation")
                            : t("common:whereAreYouGoing")
                      }
                  >
                    <InputLeftElement
                        children={<FontAwesomeIcon icon={faLocationDot}/>}
                    />
                    <Select
                        fontSize={"sm"}
                        color="gray.800"
                        bg="white"
                        style={{
                          fontSize: "sm",
                          paddingLeft: i18n.language === "en" && "2.5rem",
                          paddingRight: i18n.language === "ar" && "2.5rem",
                        }}
                        width={"full"}
                        value={reservation.place}
                        onChange={(e) => {
                          setReservation({
                            ...reservation,
                            place: capitalize(e.target.value),
                          });
                        }}
                    >
                      {(reservationType === RESERVATION_TYPES.CAR
                              ? VEHICLE_PICKUP_LOCATIONS
                              : STAY_CITIES
                      ).map((city, index) => (
                          <option key={index} value={city}>
                            {reservationType === RESERVATION_TYPES.CAR
                              ? getPickupLocationDisplayName(city, t)
                              : getCityDualDisplayName(city, t, i18n.language)}
                          </option>
                      ))}
                    </Select>
                  </InputGroupWithLabel>
              )}

              <InputGroupWithLabel label={`${t("common:chooseYourDates")} (CET)`}>
                <InputLeftElement
                    children={<FontAwesomeIcon icon={faCalendar}/>}
                />
                <RangeDatepicker
                    configs={{
                      dateFormat: "MMM dd",
                    }}
                    minDate={dateAddDaysCET(new Date(), 1)}
                    usePortal
                    propsConfigs={{
                      inputProps: {
                        paddingLeft: i18n.language === "en" ? "2.5rem" : "0",
                        paddingRight: i18n.language === "ar" ? "2.5rem" : "0",
                        fontSize: "sm",
                        cursor: "pointer",
                        color: "gray.800",
                        bg: "white",
                      },
                      dayOfMonthBtnProps: {
                        defaultBtnProps: {
                          _hover: {
                            background: "teal.400",
                          },
                        },
                        isInRangeBtnProps: {
                          background: "teal.200",
                        },
                        selectedBtnProps: {
                          background: "teal.400",
                          color: "green",
                        },
                        todayBtnProps: {
                          background: "teal.400",
                        },
                      },
                    }}
                    closeOnSelect
                    selectedDates={reservation.selectedDates}
                    onDateChange={(e) => {
                      if (reservationType === RESERVATION_TYPES.TOUR) {
                        var startDate = new Date(
                            e[0]?.getFullYear(),
                            e[0]?.getMonth(),
                            e[0]?.getDate()
                        );
                        var endDate = new Date(
                            e[1]?.getFullYear(),
                            e[1]?.getMonth(),
                            e[1]?.getDate()
                        );

                        const sevenDaysBeforeEndDate = new Date(
                            endDate.getTime() - 7 * 24 * 60 * 60 * 1000
                        );

                        if (sevenDaysBeforeEndDate < new Date(startDate)) {
                          setReservation({
                            ...reservation,
                            selectedDates: [
                              startDate,
                              new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000),
                            ],
                          });
                        } else {
                          setReservation({...reservation, selectedDates: e});
                        }
                      } else {
                        if (e[0].getTime() === e[1]?.getTime()) {
                          setReservation({
                            ...reservation,
                            selectedDates: [e[0], dateAfter(e[1])],
                          });
                        } else {
                          setReservation({...reservation, selectedDates: e});
                        }
                      }
                    }}
                />
              </InputGroupWithLabel>

              {reservationType === RESERVATION_TYPES.CAR && (
                  <>
                    <InputGroupWithLabel label={`${t("common:pickUpYourCarAt")} (CET)`}>
                      <InputLeftElement
                          children={<FontAwesomeIcon icon={faClock}/>}
                      />
                      <Select
                          style={{
                            fontSize: "sm",
                            paddingLeft: i18n.language === "en" && "2.5rem",
                            paddingRight: i18n.language === "ar" && "2.5rem",
                          }}
                          fontSize={"sm"}
                          color="gray.800"
                          bg="white"
                          isRequired
                          placeholder={t("common:pickUpTime")}
                          value={reservation.pickUpTime}
                          onChange={(e) =>
                              setReservation({
                                ...reservation,
                                pickUpTime: e.target.value,
                              })
                          }
                      >
                        {pickUpTimeInputOptions.map((time, tid) => (
                            <option key={`time-${tid}`} value={time}>
                              {time}
                            </option>
                        ))}
                      </Select>
                    </InputGroupWithLabel>

                    <InputGroupWithLabel label={`${t("common:dropOffYourCarAt")} (CET)`}>
                      <InputLeftElement
                          children={<FontAwesomeIcon icon={faClock}/>}
                      />
                      <Select
                          fontSize={"sm"}
                          color="gray.800"
                          bg="white"
                          style={{
                            fontSize: "sm",
                            paddingLeft: i18n.language === "en" && "2.5rem",
                            paddingRight: i18n.language === "ar" && "2.5rem",
                          }}
                          isRequired
                          placeholder={t("common:dropOffTime")}
                          value={reservation.dropOffTime}
                          onChange={(e) =>
                              setReservation({
                                ...reservation,
                                dropOffTime: e.target.value,
                              })
                          }
                      >
                        {pickUpTimeInputOptions.map((time, tid) => (
                            <option key={`time-${tid}`} value={time}>
                              {time}
                            </option>
                        ))}
                      </Select>
                    </InputGroupWithLabel>
                  </>
              )}

              <Popover closeOnBlur placement="bottom-start" isOpen={isOpen}>
                <PopoverTrigger>
                  <Box width="full" onClick={onToggle}>
                    <InputGroupWithLabel label={t("common:totalPeople")}>
                      <InputLeftElement
                          children={<FontAwesomeIcon icon={faPerson}/>}
                      />
                      <Input
                          fontSize={"sm"}
                          color="gray.800"
                          bg="white"
                          cursor={"pointer"}
                          placeholder={t("searchGuestsPlaceholder")}
                          value={travelersLabel}
                          isReadOnly
                      />
                    </InputGroupWithLabel>
                  </Box>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent zIndex={88} padding={"15px"}>
                    <Box>
                      {(reservationType === RESERVATION_TYPES.CAR
                              ? ["adults"]
                              : reservationType === RESERVATION_TYPES.TOUR
                                ? ["adults", "children"]
                                : ["rooms", "adults", "children"]
                      ).map((type, tid) => {
                        return (
                            <Flex
                                key={`type-${tid}`}
                                align={"baseline"}
                                justify={"space-between"}
                                mb={"20px"}
                            >
                              <Flex direction="column" align={"baseline"}>
                                <Text fontSize="sm">
                                  {getPopoverTypeLabel(type)}
                                </Text>
                              </Flex>
                              <Flex>
                                <Button
                                    size="xs"
                                    onClick={() =>
                                        setReservation({
                                          ...reservation,
                                          [type]:
                                              type === "children"
                                                  ? [...reservation[type].slice(0, -1)]
                                                  : reservation[type] - 1,
                                        })
                                    }
                                    isDisabled={
                                      type === "children"
                                          ? reservation[type].length <= 0
                                          : reservation[type] <= 1
                                    }
                                    variant="outline"
                                    colorScheme="teal"
                                >
                                  -
                                </Button>
                                <Box
                                    alignSelf="center"
                                    w={"40px"}
                                    textAlign={"center"}
                                >
                                  {type === "children"
                                      ? reservation[type].length
                                      : reservation[type]}
                                </Box>
                                <Button
                                    size="xs"
                                    onClick={() =>
                                        setReservation({
                                          ...reservation,
                                          [type]:
                                              type === "children"
                                                  ? [...reservation[type], ""]
                                                  : reservation[type] + 1,
                                        })
                                    }
                                    variant="outline"
                                    colorScheme="teal"
                                >
                                  +
                                </Button>
                              </Flex>
                            </Flex>
                        );
                      })}
                    </Box>

                    {reservation.children.length > 0 && (
                        <Box>
                          <Text mb={"10px"} fontSize="xs" color="gray.500">
                            {t("searchChildrenAgesHint")}
                          </Text>
                          <Flex gap={2} mb={"20px"} direction="row" flexWrap={"wrap"}>
                            {reservation.children.map((age, index) => (
                                <Select
                                    style={{
                                      fontSize: "sm",
                                      paddingLeft: i18n.language === "en" && "2.5rem",
                                      paddingRight: i18n.language === "ar" && "2.5rem",
                                    }}
                                    color="gray.800"
                                    bg="white"
                                    maxWidth={"48.5%"}
                                    key={`age-${index}`}
                                    flex={"50%"}
                                    size="sm"
                                    isRequired
                                    placeholder={t("searchChildAgePlaceholder")}
                                    value={age}
                                    onChange={(e) =>
                                        setReservation({
                                          ...reservation,
                                          children: [
                                            ...reservation.children.map((age, ind) => {
                                              if (ind === index) {
                                                return e.target.value;
                                              }
                                              return age;
                                            }),
                                          ],
                                        })
                                    }
                                >
                                  {childrenAgesOptions.map((age, aid) => (
                                      <option key={`age-${aid}`} value={age}>
                                        {t("searchChildAgeYears", { age })}
                                      </option>
                                  ))}
                                </Select>
                            ))}
                          </Flex>
                        </Box>
                    )}

                    <Button
                        onClick={onClose}
                        alignSelf="flex-end"
                        colorScheme="teal"
                        variant="solid"
                        size="sm"
                        width="150px"
                    >
                      {t("searchPopoverDone")}
                    </Button>
                  </PopoverContent>
                </Portal>
              </Popover>

              <Tooltip
                  label={
                    reservationType === RESERVATION_TYPES.CAR
                      ? t("searchTooltipCar")
                      : reservationType === RESERVATION_TYPES.STAY
                        ? t("searchTooltipStay")
                        : t("searchTooltipTour")
                  }
                  isDisabled={!searchButtonDisabled()}
              >
                <Button
                    width={["full", "full", "xs", "sm"]}
                    borderRadius="xl"
                    minWidth={"100px"}
                    colorScheme="teal"
                    variant="solid"
                    isLoading={isFetching}
                    isDisabled={searchButtonDisabled()}
                    onClick={() => {
                      navigate({
                        pathname: forHomePage
                            ? reservationType === RESERVATION_TYPES.TOUR
                                ? `/${reservationType}s/builder`
                                : `/${reservationType}s/search`
                            : locationRouter.pathname,
                        search: `?${createSearchParams({
                          location: reservation.place,
                          start_date: getDateInServerExpectedFormat(
                              reservation.selectedDates[0]
                          ),
                          end_date: getDateInServerExpectedFormat(
                              reservation.selectedDates[1]
                          ),
                          pick_up_time: reservation.pickUpTime,
                          drop_off_time: reservation.dropOffTime,
                          adults: reservation.adults,
                          // rooms: reservation.rooms, // tours: omitted until ChooseStayMenu needs it
                          ...(reservationType !== RESERVATION_TYPES.TOUR && {
                            rooms: reservation.rooms,
                          }),
                          children: reservation.children,
                        })}`,
                      });

                      if (!forHomePage && onSearchButtonClick) {
                        onSearchButtonClick();
                      }
                    }}
                >
                  {t("common:searchButtonText")}
                </Button>
              </Tooltip>
            </Stack>
          </Center>
        </Box>
  );
}

export default ReservationSearchForm;
